import { apiService } from './api';

// ============================================================================
// COMMENT TYPES
// ============================================================================

export interface LessonCommentDto {
  id: number;
  lessonId: number;
  contentTagId?: number;
  contentBlockId?: number;
  parentCommentId?: number | null;
  isAdminReply?: boolean;
  parentWriterName?: string;
  userId: string;
  commentText?: string; // Optional - either comment or rating is required
  rating?: number;
  isAnonymous: boolean;
  isApproved?: boolean;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt?: string;
  writerName?: string;
  writerEmail?: string;
  writerProfilePhotoUrl?: string;
  reviewerName?: string;
  lessonTitle?: string;
  lessonTitleFa?: string;
  levelTitle?: string;
  levelTitleFa?: string;
  courseTitle?: string;
  courseTitleFa?: string;
  /** Server: rating without comment text */
  ratingOnly?: boolean;
  /** False when the lesson was withdrawn from learner access. */
  isAccessible?: boolean;
}

export interface CreateLessonCommentDto {
  lessonId: number;
  parentCommentId?: number;
  commentText?: string;
  rating?: number;
  isAnonymous?: boolean;
}

export interface CommentListDto {
  comments: LessonCommentDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
}

export interface UserUnpublishedCommentCountDto {
  userId: string;
  unpublishedCount: number;
  pendingCount: number;
  rejectedCount: number;
}

export interface ApprovePendingCommentsForUsersResultDto {
  lessonCommentsApproved: number;
  libraryBookCommentsApproved: number;
  totalApproved: number;
  userCount: number;
}

export interface CommentFilterDto {
  userId?: string;
  lessonId?: number;
  isApproved?: boolean;
  statusFilter?: string; // "all", "pending", "approved", "rejected"
  includeThreadReplies?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface CommentStatisticsDto {
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  totalCount: number;
}

export interface LessonRatingSummaryDto {
  lessonId: number;
  averageRating: number | null;
  ratingCount: number;
}

export interface TopLessonRatingStatDto {
  lessonId: number;
  lessonTitle: string;
  levelTitle?: string;
  courseTitle?: string;
  averageRating: number;
  ratingCount: number;
}

export interface TopUserCommentStatDto {
  userId: string;
  displayName: string;
  email?: string;
  commentCount: number;
}

export interface LibraryBookCommentDto {
  id: number;
  libraryBookId: number;
  parentCommentId?: number | null;
  isAdminReply?: boolean;
  parentWriterName?: string;
  userId: string;
  commentText?: string;
  rating?: number;
  isAnonymous: boolean;
  isApproved?: boolean | null;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt?: string;
  writerName?: string;
  writerEmail?: string;
  writerProfilePhotoUrl?: string;
  reviewerName?: string;
  bookTitle?: string;
  bookFarsiTitle?: string;
  ratingOnly?: boolean;
  isAccessible?: boolean;
}

export interface CreateLibraryBookCommentDto {
  libraryBookId: number;
  parentCommentId?: number;
  commentText?: string;
  rating?: number;
  isAnonymous?: boolean;
}

export interface LibraryBookCommentListDto {
  comments: LibraryBookCommentDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
}

export interface LibraryBookRatingSummaryDto {
  libraryBookId: number;
  averageRating: number | null;
  ratingCount: number;
}

export interface TagBlockCommentDto {
  id: number;
  contentTagId: number;
  contentBlockId: number;
  parentCommentId?: number | null;
  isAdminReply?: boolean;
  parentWriterName?: string;
  userId: string;
  commentText?: string;
  rating?: number;
  isAnonymous: boolean;
  isApproved?: boolean | null;
  createdAt: string;
  writerName?: string;
  writerProfilePhotoUrl?: string;
  tagName?: string;
  lessonTitle?: string;
  ratingOnly?: boolean;
}

export interface CreateTagBlockCommentDto {
  contentTagId: number;
  contentBlockId: number;
  parentCommentId?: number;
  commentText?: string;
  rating?: number;
  isAnonymous?: boolean;
}

export interface TagBlockCommentListDto {
  comments: TagBlockCommentDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
}

export interface TagBlockRatingSummaryDto {
  contentTagId: number;
  contentBlockId: number;
  averageRating: number | null;
  ratingCount: number;
}

// ============================================================================
// COMMENT SERVICE
// ============================================================================

class CommentService {
  /**
   * Get approved comments for a lesson (paginated)
   */
  async getLessonComments(lessonId: number, pageNumber: number = 1, pageSize: number = 5, bypassCache?: boolean): Promise<CommentListDto> {
    const url = `/api/Comment/lesson/${lessonId}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    // Bypass cache if requested (e.g., when opening modal to get fresh data from DB)
    return apiService.get<CommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  /**
   * Create a new comment
   */
  async createComment(createDto: CreateLessonCommentDto): Promise<LessonCommentDto> {
    return apiService.post<LessonCommentDto>('/api/Comment', createDto);
  }

  /**
   * Get comment by ID
   */
  async getCommentById(id: number): Promise<LessonCommentDto> {
    return apiService.get<LessonCommentDto>(`/api/Comment/${id}`);
  }

  /**
   * Update comment approval status (admin only)
   */
  async updateCommentApproval(id: number, isApproved: boolean): Promise<LessonCommentDto> {
    return apiService.put<LessonCommentDto>(`/api/Comment/${id}/approval`, { isApproved, resetToPending: false });
  }

  /** Set approved/rejected comment back to pending (admin only) */
  async resetCommentToPending(id: number): Promise<LessonCommentDto> {
    return apiService.put<LessonCommentDto>(`/api/Comment/${id}/approval`, { resetToPending: true, isApproved: false });
  }

  /** Delete an admin-created reply row (admin only) */
  async deleteAdminReply(id: number): Promise<void> {
    await apiService.delete<void>(`/api/Comment/${id}/admin-reply`);
  }

  async updateLibraryBookCommentApproval(id: number, isApproved: boolean): Promise<LibraryBookCommentDto> {
    return apiService.put<LibraryBookCommentDto>(`/api/Comment/library-book/${id}/approval`, { isApproved, resetToPending: false });
  }

  /**
   * Approve all pending (text) comments for the selected users — lesson and library book (admin only).
   */
  async approvePendingCommentsForUsers(userIds: string[]): Promise<ApprovePendingCommentsForUsersResultDto> {
    return apiService.post<ApprovePendingCommentsForUsersResultDto>('/api/Comment/approve-pending-for-users', { userIds });
  }

  async resetLibraryBookCommentToPending(id: number): Promise<LibraryBookCommentDto> {
    return apiService.put<LibraryBookCommentDto>(`/api/Comment/library-book/${id}/approval`, { resetToPending: true, isApproved: false });
  }

  async deleteLibraryBookAdminReply(id: number): Promise<void> {
    await apiService.delete<void>(`/api/Comment/library-book/${id}/admin-reply`);
  }

  /**
   * Get current user's own comments (authenticated users)
   */
  async getMyComments(filter?: { lessonId?: number; statusFilter?: string; includeThreadReplies?: boolean; pageNumber?: number; pageSize?: number }, bypassCache?: boolean): Promise<CommentListDto> {
    const params = new URLSearchParams();
    if (filter?.lessonId) {
      params.append('lessonId', filter.lessonId.toString());
    }
    if (filter?.statusFilter) {
      params.append('statusFilter', filter.statusFilter);
    }
    if (filter?.pageNumber) {
      params.append('pageNumber', filter.pageNumber.toString());
    }
    if (filter?.pageSize) {
      params.append('pageSize', filter.pageSize.toString());
    }
    if (filter?.includeThreadReplies) {
      params.append('includeThreadReplies', 'true');
    }
    
    const queryString = params.toString();
    const url = `/api/Comment/my-comments${queryString ? `?${queryString}` : ''}`;
    // Bypass cache if requested (e.g., when opening modal to get fresh data from DB)
    return apiService.get<CommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  /**
   * Get current user's library book comments (authenticated users)
   */
  async getMyLibraryBookComments(
    filter?: { libraryBookId?: number; statusFilter?: string; includeThreadReplies?: boolean; pageNumber?: number; pageSize?: number },
    bypassCache?: boolean
  ): Promise<LibraryBookCommentListDto> {
    const params = new URLSearchParams();
    if (filter?.libraryBookId) {
      params.append('libraryBookId', filter.libraryBookId.toString());
    }
    if (filter?.statusFilter) {
      params.append('statusFilter', filter.statusFilter);
    }
    if (filter?.pageNumber) {
      params.append('pageNumber', filter.pageNumber.toString());
    }
    if (filter?.pageSize) {
      params.append('pageSize', filter.pageSize.toString());
    }
    if (filter?.includeThreadReplies) {
      params.append('includeThreadReplies', 'true');
    }

    const queryString = params.toString();
    const url = `/api/Comment/my-library-book-comments${queryString ? `?${queryString}` : ''}`;
    return apiService.get<LibraryBookCommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  /**
   * Get comments for a specific user (admin only)
   */
  async getUserComments(userId: string, filter?: CommentFilterDto, bypassCache?: boolean): Promise<CommentListDto> {
    const params = new URLSearchParams();
    if (filter?.statusFilter) {
      params.append('statusFilter', filter.statusFilter);
    } else if (filter?.isApproved !== undefined) {
      params.append('isApproved', filter.isApproved.toString());
    }
    if (filter?.pageNumber) {
      params.append('pageNumber', filter.pageNumber.toString());
    }
    if (filter?.pageSize) {
      params.append('pageSize', filter.pageSize.toString());
    }
    if (filter?.includeThreadReplies) {
      params.append('includeThreadReplies', 'true');
    }
    
    const queryString = params.toString();
    const url = `/api/Comment/user/${userId}${queryString ? `?${queryString}` : ''}`;
    // Bypass cache if requested (e.g., when opening modal to get fresh data from DB)
    return apiService.get<CommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  async getUserLibraryBookComments(userId: string, filter?: CommentFilterDto, bypassCache?: boolean): Promise<LibraryBookCommentListDto> {
    const params = new URLSearchParams();
    if (filter?.statusFilter) {
      params.append('statusFilter', filter.statusFilter);
    } else if (filter?.isApproved !== undefined) {
      params.append('isApproved', filter.isApproved.toString());
    }
    if (filter?.pageNumber) {
      params.append('pageNumber', filter.pageNumber.toString());
    }
    if (filter?.pageSize) {
      params.append('pageSize', filter.pageSize.toString());
    }
    if (filter?.includeThreadReplies) {
      params.append('includeThreadReplies', 'true');
    }

    const queryString = params.toString();
    const url = `/api/Comment/user/${userId}/library-book-comments${queryString ? `?${queryString}` : ''}`;
    return apiService.get<LibraryBookCommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  /**
   * Get unpublished comment count for a user (admin only)
   */
  async getUserUnpublishedCommentCount(userId: string, bypassCache?: boolean): Promise<UserUnpublishedCommentCountDto> {
    const url = `/api/Comment/user/${userId}/unpublished-count`;
    // Bypass cache if requested (e.g., on initial load to get fresh data from DB)
    return apiService.get<UserUnpublishedCommentCountDto>(url, bypassCache ? { cache: false } : undefined);
  }

  /**
   * Get all comments with filters (admin only)
   */
  async getCommentsWithFilter(filter: CommentFilterDto): Promise<CommentListDto> {
    const params = new URLSearchParams();
    if (filter.userId) {
      params.append('userId', filter.userId);
    }
    if (filter.lessonId) {
      params.append('lessonId', filter.lessonId.toString());
    }
    if (filter.statusFilter) {
      params.append('statusFilter', filter.statusFilter);
    } else if (filter.isApproved !== undefined) {
      params.append('isApproved', filter.isApproved.toString());
    }
    if (filter.pageNumber) {
      params.append('pageNumber', filter.pageNumber.toString());
    }
    if (filter.pageSize) {
      params.append('pageSize', filter.pageSize.toString());
    }
    
    const queryString = params.toString();
    return apiService.get<CommentListDto>(`/api/Comment/admin?${queryString}`);
  }

  /**
   * Get comment statistics (pending, approved, rejected counts) - admin only
   */
  async getCommentStatistics(): Promise<CommentStatisticsDto> {
    return apiService.get<CommentStatisticsDto>('/api/Comment/statistics');
  }

  async getLessonRatingSummary(lessonId: number, bypassCache?: boolean): Promise<LessonRatingSummaryDto> {
    const url = `/api/Comment/lesson/${lessonId}/rating-summary`;
    return apiService.get<LessonRatingSummaryDto>(url, bypassCache ? { cache: false } : undefined);
  }

  async getTopLessonsByAverageRating(take: number = 10, bypassCache?: boolean): Promise<TopLessonRatingStatDto[]> {
    const url = `/api/Comment/statistics/top-lessons-by-rating?take=${take}`;
    return apiService.get<TopLessonRatingStatDto[]>(url, bypassCache ? { cache: false } : undefined);
  }

  async getTopUsersByCommentCount(take: number = 10, bypassCache?: boolean): Promise<TopUserCommentStatDto[]> {
    const url = `/api/Comment/statistics/top-users-by-comment-count?take=${take}`;
    return apiService.get<TopUserCommentStatDto[]>(url, bypassCache ? { cache: false } : undefined);
  }

  async getLibraryBookComments(
    bookId: number,
    pageNumber: number = 1,
    pageSize: number = 5,
    bypassCache?: boolean
  ): Promise<LibraryBookCommentListDto> {
    const id = Number(bookId);
    if (!Number.isFinite(id)) throw new Error('Invalid book id');
    const url = `/api/Comment/library-book/${id}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return apiService.get<LibraryBookCommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  async createLibraryBookComment(createDto: CreateLibraryBookCommentDto): Promise<LibraryBookCommentDto> {
    return apiService.post<LibraryBookCommentDto>('/api/Comment/library-book', createDto);
  }

  async getLibraryBookRatingSummary(bookId: number, bypassCache?: boolean): Promise<LibraryBookRatingSummaryDto> {
    const id = Number(bookId);
    if (!Number.isFinite(id)) throw new Error('Invalid book id');
    const url = `/api/Comment/library-book/${id}/rating-summary`;
    return apiService.get<LibraryBookRatingSummaryDto>(url, bypassCache ? { cache: false } : undefined);
  }

  async getTagBlockComments(
    tagId: number,
    blockId: number,
    pageNumber: number = 1,
    pageSize: number = 5,
    bypassCache?: boolean
  ): Promise<TagBlockCommentListDto> {
    const url = `/api/Comment/tag-block/${tagId}/${blockId}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return apiService.get<TagBlockCommentListDto>(url, bypassCache ? { cache: false } : undefined);
  }

  async createTagBlockComment(createDto: CreateTagBlockCommentDto): Promise<TagBlockCommentDto> {
    return apiService.post<TagBlockCommentDto>('/api/Comment/tag-block', createDto);
  }

  async getTagBlockRatingSummary(tagId: number, blockId: number, bypassCache?: boolean): Promise<TagBlockRatingSummaryDto> {
    const url = `/api/Comment/tag-block/${tagId}/${blockId}/rating-summary`;
    return apiService.get<TagBlockRatingSummaryDto>(url, bypassCache ? { cache: false } : undefined);
  }

  async getMyTagBlockComments(
    filter?: { contentTagId?: number; contentBlockId?: number; statusFilter?: string; includeThreadReplies?: boolean; pageNumber?: number; pageSize?: number },
    bypassCache?: boolean
  ): Promise<TagBlockCommentListDto> {
    const params = new URLSearchParams();
    if (filter?.contentTagId) params.append('contentTagId', String(filter.contentTagId));
    if (filter?.contentBlockId) params.append('contentBlockId', String(filter.contentBlockId));
    if (filter?.statusFilter) params.append('statusFilter', filter.statusFilter);
    if (filter?.pageNumber) params.append('pageNumber', String(filter.pageNumber));
    if (filter?.pageSize) params.append('pageSize', String(filter.pageSize));
    if (filter?.includeThreadReplies) params.append('includeThreadReplies', 'true');
    const query = params.toString();
    return apiService.get<TagBlockCommentListDto>(
      `/api/Comment/my-tag-block-comments${query ? `?${query}` : ''}`,
      bypassCache ? { cache: false } : undefined
    );
  }

  async getUserTagBlockComments(userId: string, filter?: CommentFilterDto, bypassCache?: boolean): Promise<TagBlockCommentListDto> {
    const params = new URLSearchParams();
    if (filter?.statusFilter) params.append('statusFilter', filter.statusFilter);
    if (filter?.pageNumber) params.append('pageNumber', String(filter.pageNumber));
    if (filter?.pageSize) params.append('pageSize', String(filter.pageSize));
    if (filter?.includeThreadReplies) params.append('includeThreadReplies', 'true');
    const query = params.toString();
    return apiService.get<TagBlockCommentListDto>(
      `/api/Comment/user/${userId}/tag-block-comments${query ? `?${query}` : ''}`,
      bypassCache ? { cache: false } : undefined
    );
  }

  async updateTagBlockCommentApproval(id: number, isApproved: boolean): Promise<TagBlockCommentDto> {
    return apiService.put<TagBlockCommentDto>(`/api/Comment/tag-block/${id}/approval`, { isApproved, resetToPending: false });
  }

  async resetTagBlockCommentToPending(id: number): Promise<TagBlockCommentDto> {
    return apiService.put<TagBlockCommentDto>(`/api/Comment/tag-block/${id}/approval`, { resetToPending: true, isApproved: false });
  }

  async deleteTagBlockAdminReply(id: number): Promise<void> {
    await apiService.delete<void>(`/api/Comment/tag-block/${id}/admin-reply`);
  }
}

export const commentService = new CommentService();

